/*
 * Lemonade Training Oy, copy rights, all right reserved.
 * Gym tools always work on the gym's clock. Pure over the zone the page already
 * loaded: `undefined` = still loading, `null` or unusable = the gym has no zone,
 * a string = the gym's zone. Nothing falls back to the browser while loading.
 */
import { useMemo } from "react";
import { clocksDiffer, isUsableTimeZone, zoneCity } from "@lemonadetraining/contracts";
import { useBrowserZone } from "./useBrowserZone";

export interface GymZone {
  isLoading: boolean;
  /** The zone to compute in; null only while loading. UTC if neither zone is usable here. */
  zone: string | null;
  gymZone: string | null;
  hasGymZone: boolean;
  city: string | null;
  browserZone: string;
  /** Whether the gym's and this device's clocks read differently at any of these instants. */
  differs: (instantsMs: readonly number[]) => boolean;
}

/** The hook's result for a raw zone and this device's zone, without React. */
export function gymZoneOf(raw: string | null | undefined, browserZone: string): GymZone {
  const isLoading = raw === undefined;
  const gymZone = isUsableTimeZone(raw) ? raw : null;
  return {
    isLoading,
    zone: isLoading ? null : (gymZone ?? (isUsableTimeZone(browserZone) ? browserZone : "UTC")),
    gymZone,
    hasGymZone: gymZone !== null,
    city: zoneCity(gymZone),
    browserZone,
    differs: (instantsMs) => gymZone !== null && clocksDiffer(gymZone, browserZone, instantsMs),
  };
}

export function useGymZone(raw: string | null | undefined): GymZone {
  const browserZone = useBrowserZone();
  // Memoised so `differs` keeps its identity while the inputs hold.
  return useMemo(() => gymZoneOf(raw, browserZone), [raw, browserZone]);
}
